import "./App.scss";

import React, {useEffect, useState} from "react";

import {SnackbarProvider} from "notistack";
import {Provider, useDispatch, useSelector} from "react-redux";
import {Navigate, Outlet, Route, Routes, useLocation, useNavigate} from "react-router-dom";
import {PersistGate} from "redux-persist/integration/react";

import {Header} from "./components/Header";
import {LeftSide} from "./components/LeftSide";
import {AnamnezDetailPage} from "./pages/Anamnez/AnamnezDetail";
import {AnamnezEditPage} from "./pages/Anamnez/AnamnezEdit";
import {ForgotPage} from "./pages/Forgot";
import {HomePage} from "./pages/Home";
import {LoginPage} from "./pages/Login";
import {NotFoundPage} from "./pages/NotFound";
import {PatientDetailPage} from "./pages/PatientDetail";
import {PolicyPage} from "./pages/Policy";
import {ProfilePage} from "./pages/Profile";
import {ProfileSecurityPage} from "./pages/Profile/ProfileSecurity";
import {RegisterPage} from "./pages/Register";
import {VisitDetailPage} from "./pages/VisitDetail";
import {userStatusRoutine} from "./redux/routines/userRoutine";
import store, {persistor} from "./redux/store";

const ProtectedRoute = ({user, children}) => {
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AppContainer = () => {
  const auth = useSelector((state: any) => state.user.auth);
  const userStatus = useSelector((state: any) => state.user.userStatus);
  const [showHeader, setShowHeader] = useState(false);
  const [showLeftSide, setShowLeftSide] = useState(true);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    dispatch(userStatusRoutine());
  }, []);

  useEffect(() => {
    if (["/login", "/forgot", "/register", "/policy"].indexOf(location.pathname) === -1) {
      if (userStatus.isFailure) {
        navigate("/login");
      }
    }
  }, [userStatus]);

  useEffect(() => {
    if (["/login", "/forgot", "/register", "/policy"].indexOf(location.pathname) === -1) {
      setShowHeader(true);
    } else {
      setShowHeader(false);
    }

    if (location.pathname.indexOf("/profile") === -1) {
      setShowLeftSide(true);
    } else {
      setShowLeftSide(false);
    }
  }, [navigate, location]);

  return (
    <>
      <main>
        {auth.isSuccess && userStatus.isSuccess && showLeftSide && <LeftSide />}
        <div className="right-side">
          {((auth.isSuccess && userStatus.isSuccess) || showHeader) && <Header back={location.pathname.indexOf("/profile") !== -1} right />}
          {["/forgot", "/register", "/policy"].indexOf(location.pathname) !== -1 && <Header back />}
          <Routes>
            <Route
              path="/"
              element={
                <ProtectedRoute user={auth.isSuccess}>
                  <HomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute user={auth.isSuccess}>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/security"
              element={
                <ProtectedRoute user={auth.isSuccess}>
                  <ProfileSecurityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patient"
              element={
                <>
                  <Outlet></Outlet>
                </>
              }>
              <Route
                path=":userId"
                element={
                  <ProtectedRoute user={auth.isSuccess}>
                    <PatientDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path=":userId/visit/:id"
                element={
                  <ProtectedRoute user={auth.isSuccess}>
                    <VisitDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path=":userId/anamnez/:userAnamnez/:index"
                element={
                  <ProtectedRoute user={auth.isSuccess}>
                    <AnamnezEditPage />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route
              path="/visits/:id"
              element={
                <ProtectedRoute user={auth.isSuccess}>
                  <VisitDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/anamnez/:type"
              element={
                <ProtectedRoute user={auth.isSuccess}>
                  <AnamnezDetailPage />
                </ProtectedRoute>
              }
            />
            <Route path="/forgot" element={<ForgotPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/policy" element={<PolicyPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </div>
      </main>
    </>
  );
};

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <SnackbarProvider
          maxSnack={3}
          hideIconVariant
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}>
          <AppContainer />
        </SnackbarProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
